'use client';

import { ElementRef } from 'react';
import type { ComponentPropsWithoutRef } from 'react';
import * as React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';

import { cn } from '@/lib/utils';

const Popover = PopoverPrimitive.Root;

const PopoverTrigger = PopoverPrimitive.Trigger;

/**
 * Positions the panel against something that is **not** the thing that opens it
 * — a typeahead's own input, where a `Trigger` would toggle the panel on every
 * click and fight the typing it is meant to accompany. Radix measures the
 * anchor for `--radix-popover-trigger-width`, so a panel can still match the
 * field's width.
 */
const PopoverAnchor = PopoverPrimitive.Anchor;

/**
 * The panel. Portalled, so a scrolling or `overflow-hidden` ancestor cannot clip
 * it (VALE-632, VALE-347).
 *
 * ## Why it stops wheel events propagating (VALE-656)
 *
 * Portalling is what breaks wheel scrolling inside a Sheet or Dialog, and the
 * two are the same fact: a Sheet locks page scrolling with
 * `react-remove-scroll`, whose `document`-level `wheel` listener cancels any
 * wheel whose target sits **outside** its own container — and portalling is
 * precisely what puts this panel outside it. A scrollable list in here was
 * therefore unscrollable by mouse (keyboard still worked, because that path
 * scrolls programmatically).
 *
 * The library's own escape hatches (`shards`, `noIsolation`) are props on
 * `RemoveScroll`, which Radix's Dialog does not expose, so neither is reachable.
 * Stopping propagation here keeps the event from ever reaching that `document`
 * listener. Native scrolling of the panel is unaffected — it happens at the
 * target, before the event would bubble — and inside a scroll lock there is
 * nothing behind the panel that is meant to scroll anyway.
 *
 * A scrollable child should also carry `overscroll-contain`, so reaching the end
 * of the list does not chain outward.
 */
const PopoverContent = ({
  ref,
  className,
  align = 'center',
  sideOffset = 4,
  onWheel,
  ...props
}: ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> & {
  ref?: React.RefObject<ElementRef<typeof PopoverPrimitive.Content> | null>;
}) => (
  <PopoverPrimitive.Portal>
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      onWheel={(event) => {
        onWheel?.(event);
        // See the note above — do not remove without reading it.
        event.stopPropagation();
      }}
      className={cn(
        'bg-popover text-popover-foreground data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-[70] w-72 origin-(--radix-popover-content-transform-origin) rounded-md border p-4 shadow-md outline-hidden',
        className
      )}
      {...props}
    />
  </PopoverPrimitive.Portal>
);
PopoverContent.displayName =
  PopoverPrimitive.Content.displayName || 'PopoverContent';

export { Popover, PopoverTrigger, PopoverAnchor, PopoverContent };
